import { useMemo } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";

/**
 * useResponsiveColumns — derive a MUI X DataGrid `columnVisibilityModel`
 * from a per-field priority map, hiding lower-priority columns as the
 * viewport narrows.
 *
 * Tiers (match the v3 breakpoint tokens in src/style/v3-tokens.css):
 *   - "low"    → hidden at ≤1024 px (laptop and below)
 *   - "medium" → hidden at ≤768 px (tablet and below)
 *   - "high"   → always visible
 *
 * Unlisted fields default to visible.
 *
 * Usage:
 *   const columnVisibilityModel = useResponsiveColumns({
 *     id: "low",
 *     created_at: "low",
 *     status: "medium",
 *     name: "high",
 *   });
 *   <DataGrid columnVisibilityModel={columnVisibilityModel} ... />
 *
 * Column-definition migration guidance (MUI X official, column-dimensions page):
 *   - Replace fixed `width: 160` with `flex: 1, minWidth: 160`.
 *   - For columns that must keep a stable size (small ID, action buttons),
 *     keep `width` but add `minWidth` to ensure they don't get squeezed.
 *   - Combining `flex` and `width` on the same column will discard `width`.
 */
export default function useResponsiveColumns(columnPriorities = {}) {
  const isTabletDown = useMediaQuery("(max-width: 768px)", { noSsr: true });
  const isLaptopDown = useMediaQuery("(max-width: 1024px)", { noSsr: true });

  return useMemo(() => {
    const model = {};
    Object.entries(columnPriorities).forEach(([field, priority]) => {
      if (priority === "low" && isLaptopDown) model[field] = false;
      else if (priority === "medium" && isTabletDown) model[field] = false;
    });
    return model;
  }, [columnPriorities, isLaptopDown, isTabletDown]);
}
